import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { AIVoiceTableRow } from './types';
import { TableRowType } from './types';

import type { AIVoice } from 'js/src/client';
import AIVoiceAddYourOwnRow from 'js/src/components/app/VoiceSelectionModal/AIVoiceAddYourOwnRow';
import AIVoiceGroupRow from 'js/src/components/app/VoiceSelectionModal/AIVoiceGroupRow';
import AIVoiceRow from 'js/src/components/app/VoiceSelectionModal/AIVoiceRow';
import { Spinner } from 'js/src/components/common/ui/Spinner';
import type { VOICE_SELECTION_MODAL_SOURCE } from 'js/src/data/modals';

const LAST_SCROLL_TOP_KEY = 'lastScrollTop';

const saveScrollPosition = (el: HTMLDivElement) => {
    el.dataset[LAST_SCROLL_TOP_KEY] = String(el.scrollTop);
};

const isHorizontalScroll = (el: HTMLDivElement) => {
    const lastScrollTop = Number(el.dataset[LAST_SCROLL_TOP_KEY] ?? 0);
    return el.scrollTop === lastScrollTop;
};

const isOverflowing = (el: HTMLDivElement) => {
    const height = el.clientHeight;
    const scrollHeight = el.scrollHeight;
    const isOverflow = scrollHeight > height;
    return isOverflow;
};

const isNearBottom = (el: HTMLDivElement, buffer: number = 100) => {
    const height = el.clientHeight;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const isNearEnd = scrollTop + height + buffer >= scrollHeight;
    return isNearEnd;
};

const columnHelper = createColumnHelper<AIVoiceTableRow>();

type AIVoiceDataTableProps = {
    tableData: AIVoiceTableRow[];
    selectedVoice: AIVoice | null;
    isFetching?: boolean;
    fetchNextPage: VoidFunction;
    onVoiceSelected: (voice: AIVoice) => void;
    previewingSlug: string | null;
    stopPreview: () => void;
    startPreview: (slug: string) => void;
    canUsePremiumAIVoices: boolean;
    registerAudioElement: (slug: string, el: HTMLAudioElement, fallbackUrl: string | null) => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
    source: VOICE_SELECTION_MODAL_SOURCE | null;
    onToggleFavourite: (slug: string) => void;
    onEditVoice?: (slug: string) => void;
    onDeleteVoice?: (slug: string) => void;
    selectedLanguage: string | null;
    selectedLanguageDisplay: string | null;
};

const AIVoiceDataTable: React.FC<AIVoiceDataTableProps> = ({
    tableData,
    selectedVoice,
    isFetching,
    fetchNextPage,
    onVoiceSelected,
    previewingSlug,
    stopPreview,
    startPreview,
    canUsePremiumAIVoices,
    registerAudioElement,
    containerRef,
    source,
    onToggleFavourite,
    onEditVoice,
    onDeleteVoice,
    selectedLanguage,
    selectedLanguageDisplay,
}) => {
    const columns = useMemo(() => {
        const value = [];
        value.push(columnHelper.display({ id: 'voice', header: 'VOICE' }));
        value.push(columnHelper.display({ id: 'language', header: 'LANGUAGES' }));
        value.push(columnHelper.display({ id: 'tone', header: 'TONE' }));
        value.push(columnHelper.display({ id: 'style', header: 'STYLE' }));
        value.push(columnHelper.display({ id: 'favourite', header: '' }));
        return value;
    }, []);

    const table = useReactTable({
        data: tableData,
        columns,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => {
            if (row.type === TableRowType.Group) return `group-${row.id}`;
            if (row.type === TableRowType.AddYourOwn) return 'add-your-own';
            return row.voice.slug;
        },
    });

    const { rows } = table.getRowModel();

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: () => 42,
    });

    /**
     * fetchNextPage can change multiple times because of its dependency on
     * request and redux state. making it a ref removes it from the dependency
     * list preventing an infinite loop of fetching next page.
     */
    const fetchNextPageRef = useRef(fetchNextPage);
    useEffect(() => {
        fetchNextPageRef.current = fetchNextPage;
    }, [fetchNextPage]);

    const handleInfiniteScroll = useCallback<React.UIEventHandler<HTMLDivElement>>((e) => {
        const el = e.target as HTMLDivElement;
        if (isHorizontalScroll(el)) {
            return;
        }
        saveScrollPosition(el);
        if (isNearBottom(el)) {
            fetchNextPageRef.current();
        }
    }, []);

    const onScroll = useMemo(() => debounce(handleInfiniteScroll, 100), [handleInfiniteScroll]);

    useEffect(() => {
        if (tableData.length === 0) {
            return;
        }
        if (!fetchNextPageRef.current) {
            return;
        }
        if (!scrollContainerRef.current) {
            return;
        }
        const el = scrollContainerRef.current;
        if (isOverflowing(el)) {
            return;
        }

        // use ref version to prevent infinite loop
        fetchNextPageRef.current();
    }, [tableData]);

    return (
        <div
            ref={scrollContainerRef}
            className="voice-selection-table-container"
            data-testid="voice-selection-table-container"
            onScroll={onScroll}
        >
            <div className="container-fluid voice-selection-table voice-selection-table-header">
                {table.getHeaderGroups().map((headerGroup) => (
                    <div
                        key={headerGroup.id}
                        className="row h-100 align-items-center font-l5 font-l5--overline"
                    >
                        {headerGroup.headers.map((header) => (
                            <div
                                key={header.id}
                                className={classnames('color-grey-600', {
                                    'col-5': header.id === 'voice',
                                    'col-auto': header.id === 'favourite',
                                    col: header.id !== 'voice' && header.id !== 'favourite',
                                })}
                            >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div
                className="container-fluid voice-selection-table voice-selection-table-content"
                style={{ height: `${virtualizer.getTotalSize()}px` }}
            >
                {virtualizer.getVirtualItems().map((virtualRow, index) => {
                    const row = rows[virtualRow.index];
                    const data = row.original;

                    const style: React.CSSProperties = {
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
                    };

                    return (
                        <React.Fragment key={row.id}>
                            {data.type === TableRowType.Group && (
                                <AIVoiceGroupRow
                                    data={data}
                                    style={style}
                                    className="align-items-center"
                                    containerRef={containerRef}
                                />
                            )}
                            {data.type === TableRowType.AddYourOwn && (
                                <AIVoiceAddYourOwnRow
                                    style={style}
                                    className="align-items-center"
                                />
                            )}
                            {data.type === TableRowType.Voice && (
                                <AIVoiceRow
                                    data={data}
                                    style={style}
                                    className="align-items-center"
                                    isSelected={selectedVoice?.slug === data.voice.slug}
                                    onVoiceSelected={onVoiceSelected}
                                    isPreviewing={previewingSlug === data.voice.slug}
                                    stopPreview={stopPreview}
                                    startPreview={startPreview}
                                    canUsePremiumAIVoices={canUsePremiumAIVoices}
                                    containerRef={containerRef}
                                    registerAudioElement={registerAudioElement}
                                    source={source}
                                    onToggleFavourite={onToggleFavourite}
                                    onEditVoice={onEditVoice}
                                    onDeleteVoice={onDeleteVoice}
                                    selectedLanguage={selectedLanguage}
                                    selectedLanguageDisplay={selectedLanguageDisplay}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            {isFetching && (
                <div className="container-fluid voice-selection-table voice-selection-table-loader">
                    <div className="row justify-content-center">
                        <div className="col-auto pt-l5-16 pb-l5-8">
                            <Spinner className="color-primary" size="sm" />
                        </div>
                    </div>
                </div>
            )}
            {!isFetching && tableData.length === 0 && (
                <div className="container-fluid voice-selection-table voice-selection-table-empty">
                    <div className="row justify-content-center">
                        <div className="col-auto py-l5-64 color-grey-400">No Voices Found</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIVoiceDataTable;
